import React, { useState, useEffect } from 'react';
import { Typography, Box, Button, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, TablePagination, CircularProgress, IconButton, Chip,
  FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';

const UsersList = ({ viewersOnly = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [associations, setAssociations] = useState([]);
  const [selectedAssociation, setSelectedAssociation] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  
  // Determine if in viewers mode based on path or prop
  const isViewersMode = viewersOnly || location.pathname.startsWith('/viewers');
  
  // Determine if the current user is viewing as an association admin
  const isViewingAsAssociationAdmin = currentUser?.role === 'subadmin' && currentUser?.is_association_admin;

  // Check if user is superadmin and fetch associations
  useEffect(() => {
    const checkUserAndFetchAssociations = async () => {
      try {
        // Get user info from storage or context
        const userIsSuperAdmin = currentUser?.role === 'superadmin';
        setIsSuperAdmin(userIsSuperAdmin);
        
        // If superadmin, fetch associations
        if (userIsSuperAdmin) {
          const response = await api.get('/api/admin/associations');
          setAssociations(response.data);
          
          // Set the first association as selected by default if there are any
          if (response.data.length > 0) {
            setSelectedAssociation(response.data[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching associations:', error);
        toast.error('Failed to load associations. Please try again.');
      }
    };
    
    checkUserAndFetchAssociations();
  }, [currentUser]);
  
  // Fetch users when component mounts or associations/filters change
  useEffect(() => {
    if ((isSuperAdmin && selectedAssociation) || !isSuperAdmin) {
      fetchUsers();
    }
  }, [isViewersMode, selectedAssociation, isSuperAdmin]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      let response;
      let params = {};
      
      // Different fetching strategies based on user role and view mode
      if (currentUser?.role === 'subadmin') {
        if (currentUser?.is_association_admin) {
          // Association Admin should only see SubAdmins in their association
          params = { 
            role: 'subadmin',
            association_id: currentUser.association_id
          };
        } else {
          // Regular subadmin should only see their viewers
          params = {
            role: 'viewer',
            created_by_id: currentUser.id
          };
        }
      } else if (currentUser?.role === 'superadmin') {
        // SuperAdmin should only see association admins and subadmins
        params = { 
          association_id: selectedAssociation,
          role: 'subadmin'  // Only fetch subadmins (includes association admins which are flagged subadmins)
        };
      }
      
      // Make the API call with appropriate parameters
      response = await api.get('/api/admin/users', { params });
      
      // Additional client-side filtering if needed
      let filteredUsers = response.data;
      
      // For association admins, we want to show all subadmins in their association
      // Just exclude the current user from the list
      if (currentUser?.role === 'subadmin' && currentUser?.is_association_admin) {
        filteredUsers = response.data.filter(u => u.id !== currentUser.id);
      }
      
      setUsers(filteredUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAssociationChange = (event) => {
    const newAssociationId = event.target.value;
    setSelectedAssociation(newAssociationId);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddUser = () => {
    if (currentUser?.role === 'subadmin' && !currentUser?.is_association_admin) {
      // Regular subadmin can only have one ACTIVE viewer
      // Check if they already have an active viewer
      const hasActiveViewer = users.some(user => user.is_active);
      
      if (hasActiveViewer) {
        toast.warning('You can only have one active viewer at a time.');
        return;
      }
      
      navigate('/viewers/new');
    } else if (currentUser?.role === 'subadmin' && currentUser?.is_association_admin) {
      // Association admin adds a subadmin
      navigate('/admin/users/new', { state: { defaultRole: 'subadmin' } });
    } else {
      // Superadmin can add any type of user
      navigate('/admin/users/new');
    }
  };

  const handleEditUser = (id, userData) => {
    if (currentUser?.role === 'subadmin' && !currentUser?.is_association_admin) {
      // Regular subadmin editing a viewer
      navigate(`/viewers/${id}/edit`);
    } else if (currentUser?.role === 'subadmin' && currentUser?.is_association_admin && userData?.role === 'subadmin') {
      // Association admin editing a subadmin
      navigate(`/admin/users/${id}/edit`, { state: { defaultRole: 'subadmin' } });
    } else {
      // Default path (superadmin or other cases)
      navigate(`/admin/users/${id}/edit`);
    }
  };

  const handleDeleteUser = async (id) => {
    if (window.confirm('Are you sure you want to deactivate this user?')) {
      try {
        await api.delete(`/api/admin/users/${id}`);
        toast.success('User deactivated successfully');
        fetchUsers(); // Refresh the list
      } catch (error) {
        console.error('Error deactivating user:', error);
        toast.error('Failed to deactivate user');
      }
    }
  };

  // Helper function to get role display text
  const getRoleDisplay = (role, isAssociationAdmin) => {
    if (role === 'superadmin') return 'Super Admin';
    if (role === 'subadmin' && isAssociationAdmin) return 'Association Admin';
    if (role === 'subadmin') return 'Sub Admin';
    if (role === 'viewer') return 'Viewer';
    return role;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {currentUser?.role === 'subadmin' && currentUser?.is_association_admin
            ? 'All Subadmins' 
            : (currentUser?.role === 'subadmin' && !currentUser?.is_association_admin 
                ? 'My Viewers' 
                : 'Distributors')}
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={handleAddUser}
        >
          {currentUser?.role === 'subadmin' && currentUser?.is_association_admin
            ? 'Add Subadmin' 
            : (currentUser?.role === 'subadmin' && !currentUser?.is_association_admin 
                ? 'Add Viewer' 
                : 'Add Distributor')}
        </Button>
      </Box>
      
      {/* Association Dropdown for Superadmin */}
      {isSuperAdmin && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="association-select-label">Select Association</InputLabel>
            <Select
              labelId="association-select-label"
              id="association-select"
              value={selectedAssociation}
              onChange={handleAssociationChange}
              label="Select Association"
            >
              {associations.map((association) => (
                <MenuItem key={association.id} value={association.id}>
                  {association.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}
      
      {/* Show helpful message about active/inactive viewers for subadmins */}
      {currentUser?.role === 'subadmin' && !currentUser?.is_association_admin && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
          <Typography variant="body2">
            Note: You can have only one <strong>active</strong> viewer at a time. If you need to create a new viewer 
            and already have an active one, deactivate the existing viewer first.
          </Typography>
        </Paper>
      )}
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={5}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Distributor</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Association</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">No users found</TableCell>
                  </TableRow>
                ) : (
                  users
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.username}</TableCell>
                        <TableCell>
                          {/* For subadmins, always show distributor_name when available */}
                          {(user?.role === 'subadmin' && user.distributor_name) 
                            ? user.distributor_name
                            : (user.full_name || '-')}
                        </TableCell>
                        <TableCell>{user.city || '-'}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          {getRoleDisplay(user.role, user.is_association_admin)}
                        </TableCell>
                        <TableCell>{user.association_name || '-'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={user.is_active ? 'Active' : 'Inactive'} 
                            color={user.is_active ? 'success' : 'error'} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={() => handleEditUser(user.id, user)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => handleDeleteUser(user.id)}
                            disabled={user.id === currentUser?.id} // Prevent deleting yourself
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={users.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
    </Box>
  );
};

export default UsersList;